import { createFeatureSelector, createSelector } from '@ngrx/store';
import {
  PARAGRAPH_FEATURE_KEY,
  ParagraphState,
  paragraphAdapter,
} from './paragraph.reducer';
import { selectRouteParam, selectRouteParams } from '@lyceo/shared/data-access';
import { selectParamBookId, selectParamBook } from '../book/book.selectors';
import { CallState, getError, LoadingState } from '@lyceo/shared/util-loading';

export const selectParagraphState = createFeatureSelector<ParagraphState>(
  PARAGRAPH_FEATURE_KEY,
);

const { selectAll, selectEntities } = paragraphAdapter.getSelectors();

export const selectAllParagraphs = createSelector(
  selectParagraphState,
  (state: ParagraphState) => selectAll(state),
);

export const selectParagraphEntities = createSelector(
  selectParagraphState,
  selectEntities,
);

export const selectParamParagraph = createSelector(
  selectParagraphEntities,
  selectRouteParams,
  (paragraphs, { paragraphId }) => paragraphs[paragraphId],
);

export const selectParamParagraphChapterId = createSelector(
  selectParamParagraph,
  (paragraph) => paragraph?.chapterId,
);

export const selectParamParagraphId = selectRouteParam('paragraphId');

export const selectBookParamParagraphs = createSelector(
  selectAllParagraphs,
  selectParamBookId,
  (paragraphs, bookId) =>
    paragraphs.filter((paragraph) => paragraph.bookId === bookId),
);

export const selectParagraphCallState = createSelector(
  selectParagraphState,
  (state: ParagraphState) => state.paragraphCallState,
);

export const selectParagraphLoading = createSelector(
  selectParagraphCallState,
  (callState: CallState) => callState === LoadingState.LOADING,
);

export const selectParagraphError = createSelector(
  selectParagraphCallState,
  (callState: CallState) => getError(callState),
);

export const selectNextParagraph = createSelector(
  selectParagraphEntities,
  selectParamParagraph,
  (paragraphs, currentParagraph) =>
    currentParagraph && currentParagraph.nextParagraphId
      ? paragraphs[currentParagraph.nextParagraphId]
      : undefined,
);

export const selectPreviousParagraph = createSelector(
  selectParagraphEntities,
  selectParamParagraph,
  (paragraphs, currentParagraph) =>
    currentParagraph && currentParagraph.previousParagraphId
      ? paragraphs[currentParagraph.previousParagraphId]
      : undefined,
);

export const selectLastParagraph = createSelector(
  selectParagraphEntities,
  selectParamBook,
  (paragraphs, book) =>
    book && book.lastParagraphId ? paragraphs[book.lastParagraphId] : undefined,
);

export const selectParagraphFullyLoadedIds = createSelector(
  selectParagraphState,
  (state: ParagraphState) => state.fullyLoadedIds,
);

export const selectParamParagraphLoaded = createSelector(
  selectParamParagraphId,
  selectParagraphFullyLoadedIds,
  (paramParagraphId, fullyLoadedIds) =>
    paramParagraphId ? fullyLoadedIds.includes(paramParagraphId) : false,
);

export const selectParamParagraphVideoIds = createSelector(
  selectParamParagraph,
  (paragraph) => (paragraph && paragraph.videoIds ? paragraph.videoIds : []),
);

export const selectParamParagraphExerciseIds = createSelector(
  selectParamParagraph,
  (paragraph) =>
    paragraph && paragraph.exerciseIds ? paragraph.exerciseIds : [],
);
